import type { AiTransformationItem } from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import type { PrioritizeScope } from './prioritization';
import type { StrategyViewState } from './view-state';
import { strategyQueryInputs, toQueryFiltersWithoutPick } from './view-state';

/**
 * Where the page's positions sit, which a role, level or position is prioritized within. The title search and the
 * potential filter are left out: they name positions, not the tasks their prioritization takes along.
 */
export const scopeBaseFilters = (state: StrategyViewState): AiTransformationFilters => ({
    ...toQueryFiltersWithoutPick(state),
    q: undefined,
    scoreLevel: [],
});

/** A role or level: all its positions and their tasks of high potential. */
export const groupScope = (
    state: StrategyViewState,
    grouping: 'LEVEL' | 'ROLE',
    group: { readonly id: string; readonly title: string },
): PrioritizeScope => {
    const base = scopeBaseFilters(state);
    const filters = grouping === 'ROLE' ? { ...base, roleId: [group.id] } : { ...base, levelId: [group.id] };

    return { name: group.title, positions: { filters }, tasks: { filters, onlyHighPotential: true } };
};

/**
 * One position, by its id, and the tasks of high potential of its job: the tasks belong to the role and level the
 * position does. A position mapped to no role has no tasks to take along.
 */
export const positionScope = (state: StrategyViewState, item: AiTransformationItem): PrioritizeScope => {
    const base = scopeBaseFilters(state);
    if (item.role === null || item.role === undefined) {
        return { name: item.title, positions: { filters: base, ids: [item.id] } };
    }
    const filters: AiTransformationFilters = {
        ...base,
        levelId: item.level === null || item.level === undefined ? [] : [item.level.id],
        roleId: [item.role.id],
    };

    return { name: item.title, positions: { filters, ids: [item.id] }, tasks: { filters, onlyHighPotential: true } };
};

/**
 * What is picked on the matrix, under all the page's filters: in the tasks view its tasks of every potential, else its
 * positions and the tasks of high potential they hold.
 */
export const selectionScope = (state: StrategyViewState, name: string): PrioritizeScope => {
    const { filters } = strategyQueryInputs(state).list;
    if (state.view === 'tasks') return { name, tasks: { filters, onlyHighPotential: false } };

    return {
        name,
        positions: { filters },
        tasks: { filters: { ...filters, q: undefined, scoreLevel: [] }, onlyHighPotential: true },
    };
};
